// effective-config-loader.ts
//
// §18: reads global `~/.avatar/autopilot-config.json` ONCE at startup (init or
// run), validates with AutopilotConfigSchema (P4), and writes a durable
// snapshot to `<control_root>/effective-config.json`. Runtime (this module's
// callers) reads ONLY the returned frozen object thereafter — the global file
// changing mid-run has zero effect on an already-loaded config (test 46).
//
// Judgment call (documented in evidence): `mattermost` has NO top-level
// `.default()` in config-schema.ts (only its inner fields do) — a missing
// global file cannot validate against `{}` alone. The "missing file" branch
// therefore seeds a MINIMAL valid `mattermost` block using §18's own example
// values (`channel_name: "autopilot"`, empty `approvers`) before validating —
// this is not inventing new defaults, it's the smallest object that lets the
// REST of the schema's real `.default()`s apply, matching the spec's own
// example manifest's channel_name.
//
// DI pattern matches workspace-registry-store.ts: paths/readFile are
// parameters, never read `~/.avatar` directly in the body — tests never touch
// the real home directory.
//
// V2 phase 02 (spec §7): the sibling `autopilot-config.v2.json` is read in the
// same load (effective-config-v2-loader.ts) and snapshotted next to this one.

import { join } from "node:path";
import {
  type ConfigFileLoadErr,
  configFileError,
} from "./config-file-error-without-content-echo.js";
import { findUnusedKeysDeclaredInFile } from "./config-keys-not-read-in-v1.js";
import { writeFileDurable } from "./durable-file-writer.js";
import {
  findV2KeyMisplacedInMainConfig,
  loadConfigV2,
  writeConfigV2Snapshot,
} from "./effective-config-v2-loader.js";
import { type AutopilotConfig, AutopilotConfigSchema } from "./schemas/config-schema.js";
import type { AutopilotConfigV2 } from "./schemas/config-v2-schema.js";

export interface EffectiveConfigLoadOk {
  ok: true;
  config: Readonly<AutopilotConfig>;
  /** V2 keys from `autopilot-config.v2.json` (defaults when absent) — frozen like `config`. */
  configV2: Readonly<AutopilotConfigV2>;
  /** D7 (R-18): 1 line per §18 key the USER'S file declared that v1 has zero readers for — never for a key that only came from MISSING_FILE_SEED. */
  unusedKeyWarnings: string[];
}

export type EffectiveConfigLoadErr = ConfigFileLoadErr;

export type EffectiveConfigLoadResult = EffectiveConfigLoadOk | EffectiveConfigLoadErr;

export interface EffectiveConfigLoaderDeps {
  /** Reads the global config file's raw text. Rejects with `code === "ENOENT"` => treated as "missing" (seed); any OTHER error (unreadable, malformed JSON) => config_invalid, never silently seeded. */
  readGlobalConfig: () => Promise<string>;
  /** Same contract for the sibling V2 file (ENOENT ⇒ V2 defaults). */
  readGlobalConfigV2: () => Promise<string>;
}

export interface LoadEffectiveConfigInput {
  globalConfigPath: string;
  /** Where the snapshot is written; omitted ⇒ validate only (init preflight, before any mission dir exists). */
  controlRoot?: string;
  deps?: Partial<EffectiveConfigLoaderDeps>;
}

/** §18 example manifest's minimal seed for the one field with no schema default. */
const MISSING_FILE_SEED = {
  mattermost: { channel_name: "autopilot", approvers: [] },
};

async function defaultReadGlobalConfig(path: string): Promise<string> {
  const { readFile } = await import("node:fs/promises");
  return readFile(path, "utf8");
}

/**
 * Load, validate, and snapshot effective-config. Never throws for expected-bad
 * input — returns `{ok:false, fieldPath, message}` instead, so callers
 * (run/init) can block with a precise diagnostic (test 45). Only a true
 * missing file (ENOENT) falls back to the §18 seed; unreadable (EACCES, ...),
 * malformed JSON, or a schema violation are all `config_invalid` (AC-09).
 */
export async function loadEffectiveConfig(
  input: LoadEffectiveConfigInput,
): Promise<EffectiveConfigLoadResult> {
  const readGlobalConfig =
    input.deps?.readGlobalConfig ?? (() => defaultReadGlobalConfig(input.globalConfigPath));

  let rawInput: unknown;
  let usedMissingSeed = false;
  let text: string | undefined;
  try {
    text = await readGlobalConfig();
  } catch (err) {
    // Only a REAL "file does not exist" (ENOENT) falls back to the
    // documented minimal seed (test 44). Any other read failure (EACCES,
    // EISDIR, ...) is a config_invalid — silently seeding over it would hide
    // a real operator mistake as if no config had ever been set.
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") {
      return configFileError(input.globalConfigPath, "không đọc được", err);
    }
  }
  if (text === undefined) {
    rawInput = MISSING_FILE_SEED;
    usedMissingSeed = true;
  } else {
    try {
      rawInput = JSON.parse(text);
    } catch (err) {
      return configFileError(input.globalConfigPath, "JSON sai cú pháp", err);
    }
    // §7: a V2 key in the main file gets a pointer, not v1's "unrecognized key".
    const misplaced = findV2KeyMisplacedInMainConfig(rawInput, input.globalConfigPath);
    if (misplaced !== undefined) return misplaced;
  }

  const parsed = AutopilotConfigSchema.safeParse(rawInput);
  if (!parsed.success) {
    if (usedMissingSeed) {
      // The seed itself must always validate — a failure here is a bug in
      // this module, not a user config error. Surface it distinctly.
      const issue = parsed.error.issues[0];
      return {
        ok: false,
        fieldPath: issue ? issue.path.join(".") : "(unknown)",
        message: `Seed nội bộ cho global config thiếu KHÔNG hợp lệ theo config.schema — lỗi ở loader, không phải user config: ${parsed.error.message}`,
      };
    }
    const issue = parsed.error.issues[0];
    const fieldPath = issue ? issue.path.join(".") : "(unknown)";
    return {
      ok: false,
      fieldPath,
      message: `Global config "${input.globalConfigPath}" không hợp lệ ở field "${fieldPath}": ${
        issue ? issue.message : parsed.error.message
      }`,
    };
  }

  const fallbackProblem = permissionsFallbackProblem(parsed.data);
  if (fallbackProblem !== undefined) {
    return { ok: false, fieldPath: "permissions_allow_fallback", message: fallbackProblem };
  }

  // V2 file read in the same load, before any snapshot is written.
  const v2 = await loadConfigV2({
    globalConfigPath: input.globalConfigPath,
    readGlobalConfigV2: input.deps?.readGlobalConfigV2,
  });
  if (!v2.ok) return v2;

  // Deep-freeze so the returned object is a true point-in-time snapshot —
  // callers cannot accidentally mutate it, and it never re-reads the global
  // file (test 46: "not re-read mid-run").
  const config = deepFreeze(structuredClone(parsed.data)) as Readonly<AutopilotConfig>;
  const configV2 = deepFreeze(v2.configV2) as Readonly<AutopilotConfigV2>;

  if (input.controlRoot !== undefined) {
    await writeFileDurable(
      join(input.controlRoot, "effective-config.json"),
      Buffer.from(`${JSON.stringify(parsed.data, null, 2)}\n`, "utf8"),
    );
    await writeConfigV2Snapshot(input.controlRoot, configV2);
  }

  // D7: only what the USER actually wrote should ever warn — the seed
  // object above is this module's own stand-in for a missing file, never
  // something a person declared.
  const unusedKeyWarnings = usedMissingSeed ? [] : findUnusedKeysDeclaredInFile(rawInput);

  return { ok: true, config, configV2, unusedKeyWarnings };
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const key of Object.keys(value as Record<string, unknown>)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
    Object.freeze(value);
  }
  return value;
}

/**
 * H10 (close-out 2026-09-30; review P4 M5): v1 has no per-kind permissions
 * fallback — the settings profile throws on the flag (after init built the
 * worktree; swallowed by `run`'s drift reconcile). Refused wherever config is
 * loaded (init, run, doctor), before anything acts on it.
 */
export function permissionsFallbackProblem(config: AutopilotConfig): string | undefined {
  return config.permissions_allow_fallback
    ? "permissions_allow_fallback=true chưa được hỗ trợ ở v1 (quyền cấp theo loại ticket) — bỏ khoá này khỏi config."
    : undefined;
}
